import React from 'react';
import type { TechlineCaseSummary } from '@packages/domain/techline';
import { Button, Typography, TextBadge } from '@packages/ui/shared';
import { createUseStyles } from 'react-jss';
import { DATE_FORMAT } from '@web/config/constants';
import { getBadgeVariantTechline, getDisplayValue, getDisplayDate } from '@web/utils';
import { Modal } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import colors from '@packages/core/styles/colors';

interface CaseListModalContentProps {
    cases: TechlineCaseSummary[];
    isCreateCaseLoading: boolean;
    onBack: () => void;
    onCreate: () => void;
    onEdit: (item: string) => void;
    onHide: () => void;
}

const useStyles = createUseStyles({
    card: {
        backgroundColor: colors.grayOne,
        borderLeft: `4px solid ${colors.blueFour}`,
        height: '208px',
        maxHeight: '208px',
        overflow: 'hidden',
        width: '100%',
    },
    leftContainer: {
        flex: 8,
    },
    rightContainer: {
        flex: 2,
    },
    modalBody: {
        maxHeight: '600px',
        overflowY: 'auto',
    },
    customerConcern: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
    },
});

export function CaseListModalContent({
    cases,
    isCreateCaseLoading,
    onCreate,
    onEdit,
    onHide,
    onBack,
}: CaseListModalContentProps) {
    const { t } = useTranslation();
    const classes = useStyles();

    return (
        <React.Fragment>
            <Modal.Header>
                <Modal.Title>{t('techline:vehicleInfo.previousCases.title')}</Modal.Title>
            </Modal.Header>

            <Modal.Body>
                <div className={`${classes.modalBody}`}>
                    <Typography>{t('techline:vehicleInfo.previousCases.existingCaseDescription')}</Typography>
                    <div className="mb-3">
                        <Typography>{t('techline:vehicleInfo.previousCases.createCaseDescription')}</Typography>
                    </div>
                    {cases.map((caseDetails: TechlineCaseSummary) => (
                        <div className="mt-5" key={caseDetails.caseNumber}>
                            {caseDetails?.techlineCaseStatus && (
                                <div className={`${classes.card} d-flex flex-row p-4`}>
                                    <div className={`${classes.leftContainer} d-flex flex-column`}>
                                        <div className="d-flex flex-row align-items-center">
                                            <Typography variant="h6" color="blueOne">
                                                {caseDetails.caseNumber}
                                            </Typography>
                                            <div className="ml-2">
                                                <TextBadge
                                                    data-testid="techline-case-status-badge"
                                                    variant={getBadgeVariantTechline(caseDetails.techlineCaseStatus)}
                                                >
                                                    {getDisplayValue(caseDetails.techlineCaseStatus)}
                                                </TextBadge>
                                            </div>
                                            <div className="ml-2">
                                                <Typography color="brandSecondary">
                                                    {getDisplayDate(
                                                        caseDetails.modifiedOn,
                                                        DATE_FORMAT.MON_DAY_YEAR_AT_H_MM
                                                    )}
                                                </Typography>
                                            </div>
                                        </div>
                                        <div className="d-flex flex-row mt-1">
                                            <Typography variant="caption3" color="textDarkSecondary">
                                                {t('techline:vehicleInfo.previousCases.caseLabels.repairOrderNumber')}:{' '}
                                                <Typography color="textDarkSecondary">
                                                    {getDisplayValue(caseDetails.repairOrderNumber)}
                                                </Typography>
                                            </Typography>
                                        </div>
                                        <div className="d-flex flex-row">
                                            <Typography variant="caption3" color="textDarkSecondary">
                                                {t('techline:vehicleInfo.previousCases.caseLabels.caseType')}:{' '}
                                                <Typography color="textDarkSecondary">
                                                    {getDisplayValue(caseDetails.caseType)}
                                                </Typography>
                                            </Typography>
                                        </div>
                                        <div className="d-flex flex-row">
                                            <Typography variant="caption3" color="textDarkSecondary">
                                                {t('techline:vehicleInfo.previousCases.caseLabels.failureDate')}:{' '}
                                                <Typography color="textDarkSecondary">N/A</Typography>
                                            </Typography>
                                        </div>
                                        <div className="d-flex flex-row">
                                            <Typography variant="caption3" color="textDarkSecondary">
                                                {t('techline:vehicleInfo.previousCases.caseLabels.technician')}:{' '}
                                                <Typography color="textDarkSecondary">
                                                    {getDisplayValue(caseDetails.fullName)}
                                                </Typography>
                                            </Typography>
                                        </div>
                                        <div className={`${classes.customerConcern} d-flex flex-row`}>
                                            <Typography variant="caption3" color="textDarkSecondary">
                                                {t('techline:vehicleInfo.previousCases.caseLabels.customerConcern')}:{' '}
                                                <Typography color="textDarkSecondary">
                                                    {getDisplayValue(caseDetails.customerConcern)}
                                                </Typography>
                                            </Typography>
                                        </div>
                                    </div>
                                    <div className={`${classes.rightContainer} d-flex flex-column align-items-end`}>
                                        <Button
                                            variant="primary-blue-outline"
                                            onPress={() => onEdit(caseDetails.caseNumber)}
                                        >
                                            {t('techline:vehicleInfo.previousCases.editCaseButton')}
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </Modal.Body>
            <Modal.Footer>
                <div className="d-flex justify-content-between w-100">
                    <div>
                        <Button variant="ghost-blue" onPress={onBack}>
                            {t('buttons:back')}
                        </Button>
                    </div>
                    <div className="d-flex">
                        <div className="mr-2">
                            <Button variant="ghost-blue" onPress={onHide}>
                                {t('buttons:cancel')}
                            </Button>
                        </div>
                        <Button onPress={onCreate} isLoading={isCreateCaseLoading}>
                            {t('techline:vehicleInfo.previousCases.createNewCaseButton')}
                        </Button>
                    </div>
                </div>
            </Modal.Footer>
        </React.Fragment>
    );
}
